import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { useEffect } from "react";
import CanonicalTag from "@/components/CanonicalTag";
import { NeumorphicThemeProvider, useNeumorphicTheme } from "@/contexts/NeumorphicThemeContext";
import NeumorphicThemeToggle from "@/components/NeumorphicThemeToggle";
import { NeumorphicContainer } from "@/components/neumorphic";

const MissionContent = () => {
  const { theme } = useNeumorphicTheme();
  const isDark = theme === 'dark';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const mutedTextColor = isDark ? 'text-gray-300' : 'text-gray-600';
  const recessedStyles = isDark 
    ? 'bg-[#0a0a0a] shadow-[inset_8px_8px_16px_#000000,inset_-8px_-8px_16px_#141414]' 
    : 'bg-[#f0f0f0] shadow-[inset_8px_8px_16px_#d8d8d8,inset_-8px_-8px_16px_#ffffff]';

  useEffect(() => {
    // Set page title and meta description
    document.title = "Our Mission | UDA ADU";
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn about UDA ADU\'s mission to empower homeowners with wealth-building ADU strategies. We focus on ROI, legacy, and local expertise.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Learn about UDA ADU\'s mission to empower homeowners with wealth-building ADU strategies. We focus on ROI, legacy, and local expertise.';
      document.head.appendChild(meta);
    }

    // Add meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'UDA ADU mission, ADU strategy California, ROI-focused ADU, building wealth with ADUs');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'UDA ADU mission, ADU strategy California, ROI-focused ADU, building wealth with ADUs';
      document.head.appendChild(meta);
    }

    // Add WebPage structured data
    const missionSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Our Mission",
      "description": "UDA ADU exists to help California homeowners build wealth and expand space through ADUs. We aim to simplify every step from concept to completion.",
      "url": "https://www.udaadu.com/mission"
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(missionSchema);
    document.head.appendChild(script);

    // Cleanup function to remove added elements when component unmounts
    return () => {
      const existingJsonLd = document.querySelector('script[type="application/ld+json"]');
      if (existingJsonLd && existingJsonLd.textContent?.includes('"url": "https://www.udaadu.com/mission"')) {
        existingJsonLd.remove();
      }
    };
  }, []);

  return (
    <NeumorphicContainer className="min-h-screen">
      <CanonicalTag url="https://www.udaadu.com/mission" />
      <Navigation />
      <NeumorphicThemeToggle />
      
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Main Content in Recessed Container */}
        <div className={`rounded-2xl p-8 md:p-12 transition-all duration-300 ${recessedStyles}`}>
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl text-[#e4683b] mb-6 md:text-4xl font-normal">
              Our Mission: Build Smart. Live Rich.
            </h1>
            <p className={`text-xl md:text-2xl ${mutedTextColor} font-light mb-8`}>
              Every square foot has a financial future — we're here to unlock it
            </p>
          </div>

          {/* Main Content */}
          <div className="mb-16">
            <p className={`text-lg ${mutedTextColor} leading-relaxed mb-12 text-center max-w-3xl mx-auto`}>
              At UDA ADU, our mission is simple: turn unused backyard space into generational wealth. We're not just designers and builders. We are partners in strategy. Every project we take on is carefully calculated to generate returns — for your finances, your family, and your legacy.
            </p>
          </div>

          {/* Values Section */}
          <div className="mb-16">
            <div className="grid gap-6 max-w-2xl mx-auto">
              {[
                "Full-service ADU solutions: permitting to move-in",
                "Transparent cost breakdowns with ROI projections",
                "Pre-permitted plans = faster time to market",
                "Strategic design for real-world income performance",
                "Neighborhood-specific knowledge, Bay Area–born team"
              ].map((value, index) => (
                <div key={index} className="flex items-start gap-4 p-4">
                  <Check className="h-6 w-6 text-[#e4683b] flex-shrink-0 mt-0.5" />
                  <p className={mutedTextColor + " text-lg"}>{value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Quote Block */}
          <div className="text-center mb-16">
            <blockquote className="text-2xl md:text-3xl font-light text-[#e4683b] italic">
              "We don't just build. We multiply value."
            </blockquote>
          </div>

          {/* Call to Action Section */}
          <div className="text-center">
            <h2 className={`text-2xl ${textColor} mb-8 font-normal md:text-4xl`}>
              Ready to turn your backyard into your smartest asset?
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/adu-calculator" className={`px-8 py-3 text-lg rounded-xl font-medium transition-all duration-300 ${
                isDark
                  ? 'bg-[#0a0a0a] text-[#e4683b] shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#141414] hover:shadow-[10px_10px_20px_#000000,-10px_-10px_20px_#141414]'
                  : 'bg-white text-[#e4683b] shadow-[8px_8px_16px_#d8d8d8,-8px_-8px_16px_#ffffff] hover:shadow-[10px_10px_20px_#d0d0d0,-10px_-10px_20px_#ffffff]'
              }`}>Get My ROI Estimate</Link>
              <Link to="/contact-us" className={`px-8 py-3 text-lg rounded-xl font-medium transition-all duration-300 ${
                isDark
                  ? 'bg-[#0a0a0a] text-[#e4683b] shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#141414] hover:shadow-[10px_10px_20px_#000000,-10px_-10px_20px_#141414]'
                  : 'bg-white text-[#e4683b] shadow-[8px_8px_16px_#d8d8d8,-8px_-8px_16px_#ffffff] hover:shadow-[10px_10px_20px_#d0d0d0,-10px_-10px_20px_#ffffff]'
              }`}>Book Free Consultation</Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </NeumorphicContainer>
  );
};

const Mission = () => {
  return (
    <NeumorphicThemeProvider>
      <MissionContent />
    </NeumorphicThemeProvider>
  );
};

export default Mission;
